import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../../common/ThemeToggle/ThemeToggle';
import './Header.css';
import logo from'../../../assets/images/logos/burotimenobck.png';
import categoriesService from '../../../services/categories.service';

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [dropdownRef, setDropdownRef] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchAllCategories();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef && !dropdownRef.contains(event.target)) {
        setShowCategoriesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  // Toggle body scroll when menu opens/closes
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    
    return () => document.body.classList.remove('menu-open');
  }, [isMenuOpen]);

  const fetchAllCategories = async () => {
    try {
      // Fetch all categories from Firebase
      const result = await categoriesService.getAllCategories();
      
      if (result.success) {
        setAllCategories(result.categories);
      } else {
        // Fallback to fixed categories only if fetch fails
        const fixedResult = await categoriesService.getFixedCategories();
        if (fixedResult.success) {
          setAllCategories(fixedResult.categories);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCategoryClick = (categoryName) => {
    navigate(`/products?category=${encodeURIComponent(categoryName)}`);
    setShowCategoriesDropdown(false);
    setIsMenuOpen(false);
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const handleOverlayClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
        <div className="container">
          <div className="header__content">
            {/* Left Section - Navigation (Desktop only) */}
            <nav className="header__nav header__nav--left">
              <a href="/" className="header__nav-link">Accueil</a>
              <a href="/products" className="header__nav-link">Produits</a>
              
              {/* Categories Dropdown */}
              <div 
                className="header__categories-wrapper"
                ref={setDropdownRef}
              >
                <button 
                  className="header__nav-link header__categories-btn"
                  onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}
                >
                  Catégories
                  <svg 
                    className="header__categories-arrow" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    style={{
                      transform: showCategoriesDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {showCategoriesDropdown && (
                  <div className="header__categories-dropdown">
                    {allCategories.map((category) => (
                      <button
                        key={category.id}
                        className="header__category-item"
                        onClick={() => handleCategoryClick(category.name)}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Logo - Centered */}
            <div className="header__logo">
              <a href="/">
                <img 
                  src={logo}
                  alt="BuroTime Logo" 
                  className="header__logo-img"
                />
              </a>
            </div>

            {/* Right Section - Navigation & Actions */}
            <div className="header__right">
              <nav className="header__nav header__nav--right">
                <a href="/about" className="header__nav-link">À Propos</a>
                <a href="/contact" className="header__nav-link">Contact</a>
              </nav>

              <div className="header__actions">
                {/* Mobile Menu Toggle - LEFT SIDE */}
                <button 
                  className="header__menu-toggle"
                  onClick={toggleMenu}
                  aria-label="Toggle menu"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="12" x2="21" y2="12"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <line x1="3" y1="18" x2="21" y2="18"/>
                  </svg>
                </button>

                {/* Search Icon 
                <button className="header__action-btn" aria-label="Search">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                </button>
*/}
                {/* Theme Toggle */}
                <ThemeToggle />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Sidebar - LEFT SIDE */}
      {isMenuOpen && (
        <>
          {/* Overlay */}
          <div className="header__mobile-overlay" onClick={handleOverlayClick} />
          
          {/* Sidebar */}
          <div className="header__mobile-menu">
            {/* Sidebar Header */}
            <div className="header__mobile-header">
              <div className="header__mobile-logo">Menu</div>
              <button 
                className="header__mobile-close"
                onClick={toggleMenu}
                aria-label="Close menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Navigation Links */}
            <a href="/" className="header__mobile-link" onClick={handleNavClick}>
              Accueil
            </a>
            <a href="/products" className="header__mobile-link" onClick={handleNavClick}>
              Produits
            </a>
            
            {/* Mobile Categories */}
            <div className="header__mobile-categories">
              <div className="header__mobile-categories-label">
                Catégories
              </div>
              {allCategories.map((category) => (
                <button
                  key={category.id}
                  className="header__mobile-category-item"
                  onClick={() => handleCategoryClick(category.name)}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            <a href="/about" className="header__mobile-link" onClick={handleNavClick}>
              À Propos
            </a>
            <a href="/contact" className="header__mobile-link" onClick={handleNavClick}>
              Contact
            </a>
          </div>
        </>
      )}
    </>
  );
};

export default Header;